'use client';

import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { apiFetch } from '../lib/api';
import { persistAuthStorage } from '../lib/authStorage';
import { useToast } from './Toast';

/**
 * Banner shown when the current session is an impersonation session.
 * Displays the admin email and a "Stop impersonation" button that calls
 * the backend /api/auth/stop-impersonation endpoint to return to the admin session.
 */
export default function ImpersonationBanner() {
  const { impersonatedBy, email, mounted } = useAuth();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [stopping, setStopping] = useState(false);

  if (!mounted || !impersonatedBy) return null;

  const handleStop = async () => {
    setStopping(true);
    try {
      const res = await apiFetch<{
        id: number; token: string; tokenExp?: number | null; email: string; role: string;
        roles?: string[]; permissions?: string[];
        firstName: string | null; lastName: string | null;
        impersonatedBy?: string | null;
      }>('/api/auth/stop-impersonation', { method: 'POST' });

      persistAuthStorage(res);
      window.localStorage.removeItem('adminBackup');
      showToast(t('impersonationStopped'), 'success');
      window.location.href = '/';
    } catch (e) {
      showToast(e instanceof Error ? e.message : t('impersonateError'), 'error');
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="bg-ochre/20 border-b border-ochre/30 text-ochre px-4 py-2 text-sm flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <span className="font-semibold">🎭 {t('impersonatingBanner')}</span>
        <span className="text-ochre">
          {t('impersonatingAs').replace('{email}', email || '')}
          {' '}
          ({t('impersonatingAdmin').replace('{admin}', impersonatedBy)})
        </span>
      </div>
      <button
        onClick={handleStop}
        disabled={stopping}
        className="px-3 py-1 bg-ochre hover:bg-ochre text-cream rounded text-xs font-semibold transition disabled:opacity-50 inline-flex items-center gap-1"
      >
        {stopping && (
          <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        )}
        {t('stopImpersonation')}
      </button>
    </div>
  );
}
